// UserProfileInfo.js
import { connect } from 'react-redux';
import { goBack } from 'redux-first-history';

import {
  SUBSCRIPTION_CURRENCY,
  USER_HASHTAG,
  FEATURE_DISPLAY_NAME,
} from '../RemoteConfigKeys.js';

import ProfileInfo from '../component/ProfileInfo.jsx';

import getMeData from '../selector/getMeData.js';
import getUserData from '../selector/getUserData.js';
import getListData from '../selector/getListData.js';
import getUserBadges from '../selector/getUserBadges.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getShortLinkData from '../selector/getShortLinkData.js';
import getModalData from '../selector/getModalData.js';
import getMessageList from '../selector/getMessageList.js';
import getFeedName from '../selector/getFeedName.js';
import getOperationData from '../selector/getOperationData.js';
import { getSelectedQueryOptionObject } from '../selector/getFeedQueryObject.js';

import copyTextWithToastr from '../action/copyTextWithToastr.js';
import sendFollowUser from '../action/sendFollowUser.js';
import sendUnfollowUser from '../action/sendUnfollowUser.js';
import sendChatStart from '../action/sendChatStart.js';
import fetchUserFollowersTotalCount from '../action/fetchUserFollowersTotalCount.js';
import fetchUserFollowingTotalCount from '../action/fetchUserFollowingTotalCount.js';
import fetchUserIsFollowing from '../action/fetchUserIsFollowing.js';
import fetchFeedsTotalCount from '../action/fetchFeedsTotalCount.js';
import fetchUser from '../action/fetchUser.js';
import fetchUserIsBlocked from '../action/fetchUserIsBlocked.js';
import fetchShortLink from '../action/fetchShortLink.js';
import toggleModal from '../action/toggleModal.js';

import { ProfileField, ProfileListType } from '../resource/profileConstants.js';
import getShareLink from '../resource/getShareLink.js';
import { SHOP_ID, ProductType } from '../resource/shopConstants.js';
import { PROFILE_FEED_REMOTE_CONFIG_KEY_MAP } from '../resource/profileConstants.js';
import i18n from '../resource/i18n.js';

const mapStateToProps = (state, { userId }) => {
  const meId = getMeData(state, 'id');
  const shareLink = getShareLink({
    pathname: `/user/${userId}`,
  });
  const shortShareLink = shareLink ? getShortLinkData(state, shareLink) : null;
  const defaultSubscriptionCurrency = getRemoteConfigData(
    state,
    SUBSCRIPTION_CURRENCY
  );
  const feedNameRemoteConfigKey =
    PROFILE_FEED_REMOTE_CONFIG_KEY_MAP[ProfileListType.POST];
  const outboxFeedName = getFeedName(
    state,
    feedNameRemoteConfigKey,
    '{USER_ID}',
    userId
  );
  const { feedNameWithQuery } = getSelectedQueryOptionObject(
    state,
    outboxFeedName
  );
  const sourceLanguage = getUserData(state, userId, 'source_language');
  const currentLanguage =
    getOperationData(state, ['config'], 'language') || i18n.language;
  return {
    feedNameWithQuery,
    meId,
    shareLink,
    shortShareLink,
    username: getUserData(state, userId, 'username'),
    displayName: getUserData(state, userId, 'displayName'),
    biography: getUserData(state, userId, ProfileField.BIOGRAPHY),
    originalBiography: getUserData(state, userId, 'originalBiography'),
    followingCount: getListData(
      state,
      ['user', userId, 'following'],
      'totalCount'
    ),
    followerCount: getListData(
      state,
      ['user', userId, 'follower'],
      'totalCount'
    ),
    postCount: getMessageList(
      state,
      'outbox',
      ['outbox', ProfileListType.POST, userId],
      'totalCount'
    ),
    lastModifiedAtUnix: getUserData(state, userId, 'lastModifiedAtUnix'),
    forbiddenWords: getRemoteConfigData(state, 'forbiddenWords'),
    isFollowing: getUserData(state, userId, 'isFollowing'),
    deviceWidth: getOperationData(state, ['device'], 'width'),
    isDisplayNameEnabled: getRemoteConfigData(state, FEATURE_DISPLAY_NAME),
    isProfileMoreModalOpen: ['display', 'opening'].includes(
      getModalData(state, 'ProfileMoreModal', 'status')
    ),
    hasSwaggerBadge: getUserBadges(state, userId, 'hasSwaggerBadge'),
    shouldFetchUserTranslation: true, // For withFetchTranslation
    badges: getUserBadges(state, userId, 'badges'),
    languageBadges: getUserBadges(state, userId, 'langs'),
    subscriptionProductIds: getListData(
      state,
      [
        'shop',
        SHOP_ID.subscription,
        defaultSubscriptionCurrency,
        ProductType.SUBSCRIPTIONS,
        userId,
      ],
      'itemIds'
    ),
    shouldShowUserHashtags: getRemoteConfigData(state, USER_HASHTAG),
    currentLanguage,
    sourceLanguage,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    copyTextWithToastr: ({
      copiedText,
      successfulTextKey,
      failedTextKey,
      textKeyNamespace,
    }) =>
      dispatch(
        copyTextWithToastr({
          copiedText,
          successfulTextKey,
          failedTextKey,
          textKeyNamespace,
        })
      ),
    goBack: () => dispatch(goBack()),
    sendFollowUser: ({ userId }) => dispatch(sendFollowUser({ id: userId })),
    sendUnfollowUser: ({ userId }) =>
      dispatch(sendUnfollowUser({ id: userId })),
    sendChatStart: ({ userId }) => dispatch(sendChatStart({ userId })),
    fetchUserFollowersTotalCount: ({ userId }) =>
      dispatch(fetchUserFollowersTotalCount({ userId })),
    fetchUserFollowingTotalCount: ({ userId }) =>
      dispatch(fetchUserFollowingTotalCount({ userId })),
    fetchUserIsFollowing: ({ userId }) =>
      dispatch(fetchUserIsFollowing({ userId })),
    fetchFeedsTotalCount: payload => dispatch(fetchFeedsTotalCount(payload)),
    fetchUser: ({ userId, httpProxyHeaders }) =>
      dispatch(fetchUser({ id: userId, httpProxyHeaders })),
    fetchUserIsBlocked: ({ userId }) =>
      dispatch(fetchUserIsBlocked({ userId })),
    fetchShortLink: ({ location }) => dispatch(fetchShortLink({ location })),
    toggleModal: ({ modalId, payload, isOpen }) =>
      dispatch(toggleModal({ modalId, payload, isOpen })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileInfo);
